/**
 * Turunan dinamika penduduk.
 *
 * Empat angka mentah (kelahiran, kematian, pindah masuk, pindah keluar) hanya
 * bercerita setengah: yang menentukan penduduk bertambah atau berkurang adalah
 * selisihnya. Helper ini menghitung selisih itu tanpa data tambahan apa pun dari
 * luar — murni turunan dari angka yang sudah ditampilkan kartu.
 */

/** Arah perubahan, dipakai komponen untuk memilih warna & tanda. */
export type DynamicsTrend = "naik" | "turun" | "tetap";

export interface DynamicsNet {
	/** Kelahiran − kematian. */
	natural: number;
	/** Pindah masuk − pindah keluar. */
	migration: number;
	/** Jumlah keduanya: perubahan penduduk secara keseluruhan. */
	total: number;
}

export interface DynamicsInput {
	births: number;
	deaths: number;
	moveIn: number;
	moveOut: number;
}

/** Nilai non-finite (NaN/Infinity) dianggap 0 supaya ringkasan tidak ikut rusak. */
function safe(value: number): number {
	return Number.isFinite(value) ? value : 0;
}

export function calcDynamicsNet({
	births,
	deaths,
	moveIn,
	moveOut,
}: DynamicsInput): DynamicsNet {
	const natural = safe(births) - safe(deaths);
	const migration = safe(moveIn) - safe(moveOut);
	return { natural, migration, total: natural + migration };
}

export function trendOf(value: number): DynamicsTrend {
	if (value > 0) return "naik";
	if (value < 0) return "turun";
	return "tetap";
}

/**
 * Format bertanda eksplisit: `+1`, `-2`, `0`. Tanda plus sengaja ditampilkan
 * supaya angka positif tidak terbaca sebagai jumlah absolut.
 */
export function formatSigned(value: number): string {
	const n = safe(value);
	if (n > 0) return `+${n.toLocaleString("id-ID")}`;
	return n.toLocaleString("id-ID");
}
